import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent } from './card';
import { Upload, File } from 'lucide-react';

interface DropzoneProps {
  onFileSelect: (files: File[]) => void;
  onFileRemove: (index: number) => void;
  selectedFiles: File[];
  accept?: Record<string, string[]>;
  maxSize?: number;
  className?: string;
  multiple?: boolean;
}

const Dropzone: React.FC<DropzoneProps> = ({
  onFileSelect,
  onFileRemove,
  selectedFiles,
  accept = {
    'text/*': ['.txt', '.md', '.doc', '.docx'],
    'application/pdf': ['.pdf'],
    'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
  },
  maxSize = 21 * 1024 * 1024, // 21MB
  multiple = true,
  className = '',
}) => {
  const [isDragActive, setIsDragActive] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        // Add new files to existing ones
        onFileSelect([...selectedFiles, ...acceptedFiles]);
      }
    },
    [onFileSelect, selectedFiles]
  );

  const { getRootProps, getInputProps, isDragReject } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };


  return (
    <Card
      {...getRootProps()}
      className={`
        border-2 border-dashed cursor-pointer transition-colors
        ${isDragActive && !isDragReject
          ? 'border-chart-4/30 bg-chart-4/10'
          : isDragReject
          ? 'border-destructive/30 bg-destructive/10'
          : 'border-input hover:border-input/80 hover:bg-muted'
        }
        ${className}
      `}
    >
      <CardContent className="p-8">
        <input {...getInputProps()} />
        <div className="text-center">
          <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-4">
            <Upload className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-card-foreground mb-2">
            {isDragActive ? 'Drop your files here' : 'Upload files'}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Drag and drop your files here, or click to browse
          </p>
          <div className="text-xs text-muted-foreground">
            <p>Supported formats: PDF, DOC, DOCX, TXT, MD, Images</p>
            <p>Max file size: {formatFileSize(maxSize)} per file</p>
            <p>Multiple files supported</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Dropzone;
